import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import { Grid, IconButton, TextField, Typography } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { da, enUS, pl } from "date-fns/locale";
import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLanguage } from "../../utils";

export const DatePeriodSelector = (props) => {
    const {
        startDate,
        endDate,
        excludedDates = [],
        onStartDateChange,
        onEndDateChange,
        readOnly = false,
        maxDuration,
    } = props;
    const { t } = useTranslation();
    const language = useLanguage();
    if (!readOnly && (!onStartDateChange || !onEndDateChange)) {
        throw new Error(
            "onStartDateChange and onEndDateChange must be provided when component is not readOnly",
        );
    }

    const today = new Date();
    const currentDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    const isExcludedDate = (date) => {
        return excludedDates.some((d) => d.getTime() === date.getTime());
    };

    const shouldDisableDate = (date) => {
        if (date < currentDate) {
            return true;
        }

        if (isExcludedDate(date)) {
            return true;
        }

        // don't allow to make reservation on weekend
        // if (isWeekend(date)) {
        //     return true;
        // }

        return false;
    };

    useEffect(() => {
        if (readOnly) {
            return;
        }

        if (
            startDate &&
            endDate &&
            !isPeriodValid(startDate, endDate, excludedDates, maxDuration)
        ) {
            const durationMillis = endDate.getTime() - startDate.getTime();
            if (durationMillis < 0) {
                onStartDateChange?.(null);
                onEndDateChange?.(null);
                return;
            }

            const d = new Date();
            d.setHours(0, 0, 0, 0);

            for (let i = 0; i < 365; i++) {
                const currentEndDate = new Date(d.getTime() + durationMillis);
                if (isPeriodValid(d, currentEndDate, excludedDates, maxDuration)) {
                    onStartDateChange?.(d);
                    onEndDateChange?.(currentEndDate);
                    return;
                }
                d.setDate(d.getDate() + 1);
            }

            onStartDateChange?.(null);
            onEndDateChange?.(null);
        }
    }, [readOnly, JSON.stringify(excludedDates), startDate, endDate, maxDuration]);

    const millisInDay = 24 * 60 * 60 * 1000;
    const numberOfDays =
        startDate && endDate
            ? Math.ceil((endDate.getTime() - startDate.getTime()) / millisInDay) + 1
            : 0;


    const minStartDate = useMemo(()=>{
        if (!endDate) {
            return null
        }

        let currDate = new Date(endDate)
        let numOfWorkingDays = maxDuration - 1         
        while (numOfWorkingDays > 0 || isWeekend(currDate)) {
            if (!isWeekend(currDate)) {
                numOfWorkingDays -= 1
            } 

            currDate.setDate(currDate.getDate() - 1)
   
        }
        return currDate

    }, [endDate])        

    const shouldDisableDateForStart = (date) => {
        if (endDate && date > endDate) {
            return true;
        }

        // if (endDate != null && maxDuration != null) {
        //     const newNumberOfDays =
        //         Math.ceil((endDate.getTime() - date.getTime()) / millisInDay) + 1;
        //     if (newNumberOfDays <= maxDuration) {
        //         return shouldDisableDate(date);
        //     }

        //     let currDate = new Date(date)
        //     let numOfWorkingDays = 0            
        //     while (currDate.getDate() <= endDate.getDate()) {
        //         if (!isWeekend(currDate)) {
        //             numOfWorkingDays += 1
        //         } 

        //         currDate.setDate(currDate.getDate() + 1)
        //     }

        //     if (numOfWorkingDays > maxDuration) {
        //         return true;
        //     }

        //     return shouldDisableDate(date)
        // }

        if (endDate != null && maxDuration != null && minStartDate != null) {
            if (date < minStartDate) {
                return true
            }

            return shouldDisableDate(date)
        }

        return shouldDisableDate(date);
    };

    const maxEndDate = useMemo(()=>{
        if (!startDate) {
            return null
        }

        let currDate = new Date(startDate)
        let numOfWorkingDays = maxDuration - 1         
        while (numOfWorkingDays > 0 || isWeekend(currDate)) {
            if (!isWeekend(currDate)) {
                numOfWorkingDays -= 1
            } 

            currDate.setDate(currDate.getDate() + 1)
   
        }
        return currDate

    }, [startDate])


    const shouldDisableDateForEnd = (date) => {
        if (startDate && date < startDate) {
            return true;
        }

        // if (startDate != null && maxDuration != null) {

        //     const newNumberOfDays =
        //         Math.ceil((date.getTime() - startDate.getTime()) / millisInDay) + 1;

        //     if (newNumberOfDays <= maxDuration) {
        //         return shouldDisableDate(date);
        //     }

        //     let currDate = new Date(startDate)
        //     let numOfWorkingDays = 0            
        //     while (currDate.getDate() <= date.getDate()) {
        //         if (!isWeekend(currDate)) {
        //             numOfWorkingDays += 1
        //         } 

        //         if (isWeekend(currDate) && numOfWorkingDays === maxDuration) {
        //             return true
        //         }
        //         currDate.setDate(currDate.getDate() + 1)
        //     }

        //     if (numOfWorkingDays > maxDuration) {
        //         return true;
        //     }

        //     return shouldDisableDate(date)
        // }

        if (startDate != null && maxDuration != null && maxEndDate != null) {

            if (date > maxEndDate) {
                return true
            }

            return shouldDisableDate(date)
        }

        return shouldDisableDate(date);
    };

    const handleStartDateChange = (newStart) => {
        onStartDateChange?.(newStart);
    };

    const handleEndDateChange = (newEnd) => {
        onEndDateChange?.(newEnd);
    };

    const numberOfDaysLabel =
        maxDuration != null
            ? `${t("DatePeriodSelector.numberOfDays")} (max. ${maxDuration})`
            : t("DatePeriodSelector.numberOfDays");

    return (
        <LocalizationProvider
            dateAdapter={AdapterDateFns}
            adapterLocale={language === "pl" ? pl : enUS}
        >
            <Grid container direction="row" alignItems="center" justifyContent="center">
                <Grid item xs={8} container direction="column" spacing={1}>
                    <Grid item>
                        <Typography variant="subtitle1">
                            {t("DatePeriodSelector.period")}
                        </Typography>
                    </Grid>
                    <Grid item container direction="row" spacing={1}>
                        <Grid item xs={6}>
                            <Grid container direction="row" alignItems="center" spacing={1}>
                                <Grid item xs>
                                    <DatePicker
                                        value={startDate}
                                        onChange={handleStartDateChange}
                                        renderInput={(params) => (
                                            <TextField fullWidth {...params} />
                                        )}
                                        shouldDisableDate={shouldDisableDateForStart}
                                        disabled={readOnly || !onStartDateChange}
                                    />
                                </Grid>
                                {!readOnly && (
                                    <Grid item>
                                        <IconButton onClick={() => onStartDateChange?.(null)}>
                                            <ClearOutlinedIcon />
                                        </IconButton>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                        <Grid item xs={6}>
                            <Grid container direction="row" alignItems="center" spacing={1}>
                                <Grid item xs>
                                    <DatePicker
                                        value={endDate}
                                        onChange={handleEndDateChange}
                                        renderInput={(params) => (
                                            <TextField fullWidth {...params} />
                                        )}
                                        shouldDisableDate={shouldDisableDateForEnd}
                                        disabled={readOnly || !onEndDateChange}
                                        defaultCalendarMonth={startDate || undefined}
                                    />
                                </Grid>
                                {!readOnly && (
                                    <Grid item>
                                        <IconButton onClick={() => onEndDateChange?.(null)}>
                                            <ClearOutlinedIcon />
                                        </IconButton>
                                    </Grid>
                                )}
                            </Grid>
                        </Grid>
                    </Grid>
                </Grid>
                <Grid item xs={4} container direction="column" spacing={1}>
                    <Grid item>
                        <Typography variant="subtitle1">{numberOfDaysLabel}</Typography>
                    </Grid>
                    <Grid item>
                        <TextField
                            disabled
                            value={numberOfDays}
                            inputProps={{ style: { textAlign: "center" } }}
                            style={{ maxWidth: "80px" }}
                        />
                    </Grid>
                </Grid>
            </Grid>
        </LocalizationProvider>
    );
};
const isWeekend = (date) => {
    const day = date.getDay();
    return day === 0 || day === 6; // 0 = Sunday, 6 = Saturday
};
const doesPeriodIncludeExcludedDates = (start, end, excludedDates) => {
    if (!start || !end) {
        return false;
    }

    return excludedDates.some((date) => {
        const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
        return d >= start && d <= end;
    });
};
export const isPeriodValid = (startDate, endDate, excludedDates, maxDuration) => {
    if (!startDate || !endDate) {
        return false;
    }

    if (!excludedDates) {
        excludedDates = [];
    }

    if (startDate > endDate) {
        return false;
    }

    // don't allow to make reservation on weekend
    // if (isWeekend(startDate) || isWeekend(endDate)) {
    //     return false;
    // }

    const today = new Date();
    const currentDate = new Date(today.getFullYear(), today.getMonth(), today.getDate());

    if (startDate < currentDate || endDate < currentDate) {
        return false;
    }

    if (doesPeriodIncludeExcludedDates(startDate, endDate, excludedDates)) {
        return false;
    }

    // if (maxDuration != null) {
    //     const millisInDay = 24 * 60 * 60 * 1000;
    //     const numberOfDays = Math.ceil((endDate.getTime() - startDate.getTime()) / millisInDay) + 1;
    //     if (numberOfDays > maxDuration) {
    //         return false;
    //     }
    // }

    return true;
};
